import React from 'react';
import './Lifecycle.css';

const Lifecycle = ({ item, workflowStages = [], tasks = [], navigate }) => {
  const parseStageHistory = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string' && raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const normalizeStageEntry = (entry) => {
    if (!entry || typeof entry !== 'object') return null;

    const normalizedStage = entry.stage ?? entry.stage_name ?? entry.stageName ?? null;
    const normalizedTimestamp = entry.timestamp ?? entry.changed_on ?? entry.changedOn ?? null;
    const normalizedChangedBy = entry.changed_by ?? entry.changedBy ?? entry.user ?? entry.user_name ?? entry.userName ?? null;

    return {
      ...entry,
      stage: normalizedStage,
      timestamp: normalizedTimestamp,
      changed_by: normalizedChangedBy ?? entry.changed_by
    };
  };

  // Parse + normalize stage history from the item
  const stageHistory = parseStageHistory(item?.stage_history)
    .map(normalizeStageEntry)
    .filter(Boolean);
  
  // If no stage history exists, return a message
  if (!stageHistory.length) {
    return (
      <div className="lifecycle-container">
        <h4>Stage History</h4>
        <p className="no-history">No stage transitions recorded.</p>
      </div>
    );
  }
  
  // Calculate duration for each stage
  const calculateDuration = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diff = end - start;
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) {
      return `${days}d ${hours}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // Detect if a stage change is a revert (going backwards)
  const isRevert = (currentStage, previousStage, allStages) => {
    if (!previousStage) return false;
    
    const currentOrder = allStages.find(s => s.stage_name === currentStage)?.stage_order;
    const previousOrder = allStages.find(s => s.stage_name === previousStage)?.stage_order;
    
    return currentOrder < previousOrder;
  };

  // Get tasks that occurred during each stage period
  const getTasksForStagePeriod = (stageStart, stageEnd) => {
    const startTime = new Date(stageStart);
    const endTime = stageEnd ? new Date(stageEnd) : new Date();
    
    return tasks.filter(task => {
      const taskCreated = new Date(task.created_on);
      const taskUpdated = new Date(task.updated_on);
      
      // Check if task was assigned (created) during this stage
      const assignedInStage = taskCreated >= startTime && taskCreated <= endTime;
      
      // Check if task was completed during this stage (status changed to Completed and updated_on is in range)
      const completedInStage = task.status === 'Completed' && taskUpdated >= startTime && taskUpdated <= endTime && taskUpdated > taskCreated;
      
      return assignedInStage || completedInStage;
    }).map(task => ({
      ...task,
      taskCreated: new Date(task.created_on),
      taskUpdated: new Date(task.updated_on),
      assignedInStage: new Date(task.created_on) >= startTime && new Date(task.created_on) <= endTime,
      completedInStage: task.status === 'Completed' && new Date(task.updated_on) >= startTime && new Date(task.updated_on) <= endTime && new Date(task.updated_on) > new Date(task.created_on)
    }));
  };
  
  // Create timeline data with revert detection and close/reopen handling
  const timelineData = stageHistory.map((entry, index) => {
    const nextEntry = stageHistory[index + 1];
    const previousEntry = stageHistory[index - 1];
    const isCurrentStage = index === stageHistory.length - 1;
    const isStageRevert = isRevert(entry.stage, previousEntry?.stage, workflowStages);
    
    // Check if this is a close or reopen event
    const isCloseEvent = entry.stage === 'Closed';
    const isReopenEvent = entry.stage === 'Reopened';
    const isStatusEvent = isCloseEvent || isReopenEvent;
    
    // Get tasks for this stage period (skip for status events)
    const stageTasks = isStatusEvent ? [] : getTasksForStagePeriod(entry.timestamp, nextEntry?.timestamp);
    
    return {
      ...entry,
      duration: calculateDuration(entry.timestamp, nextEntry?.timestamp),
      isCurrentStage,
      isRevert: isStageRevert,
      isCloseEvent,
      isReopenEvent,
      isStatusEvent,
      stageName: entry.stage,
      tasks: stageTasks
    };
  });
  
  // Get total duration
  const totalDuration = calculateDuration(timelineData[0]?.timestamp, new Date());
  
  return (
    <div className="lifecycle-container">
      <h4>Stage History</h4>
      
      <div className="timeline">
        {timelineData.map((stage, index) => (
          <div 
            key={index} 
            className={`timeline-entry ${stage.isCurrentStage ? 'current' : ''} ${stage.isRevert ? 'revert' : ''} ${stage.isCloseEvent ? 'close-event' : ''} ${stage.isReopenEvent ? 'reopen-event' : ''}`}
          >
            <div className="stage-info">
              <span className="stage-name">
                {stage.isRevert && '← '}
                {stage.isCloseEvent && '✕ '}
                {stage.isReopenEvent && '↻ '}
                {stage.stageName}
                {stage.isCurrentStage && ' (current)'}
              </span>
              <span className="stage-duration">{stage.duration}</span>
            </div>
            <div className="stage-meta">
              <span className="timestamp">
                {(() => {
                  const date = stage.timestamp ? new Date(stage.timestamp) : null;
                  if (!date || Number.isNaN(date.getTime())) return '—';
                  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}`;
                })()}
              </span>
              {stage.changed_by && <span className="user">{stage.changed_by}</span>}
              {stage.user_id && !stage.changed_by && <span className="user">User {stage.user_id}</span>}
            </div>
            
            {/* Display tasks for this stage */}
            {stage.tasks && stage.tasks.length > 0 && (
              <div className="stage-tasks">
                {stage.tasks.map(task => (
                  <div key={task.id} className="task-item">
                    {task.assignedInStage && (
                      <div className="task-action assigned">
                        📋 <span 
                          className="task-link" 
                          onClick={() => navigate && navigate(`/tasks/detail/${task.id}`)}
                        >
                          Task #{task.id}: {task.description}
                        </span> assigned
                      </div>
                    )}
                    {task.completedInStage && (
                      <div className="task-action completed">
                         <span 
                          className="task-link"
                          onClick={() => navigate && navigate(`/tasks/detail/${task.id}`)}
                        >
                          Task #{task.id}: {task.description}
                        </span> completed
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      
      <div className="summary">
        <span>Total: {totalDuration}</span>
        <span>{timelineData.filter(t => !t.isRevert && !t.isStatusEvent).length} advances, {timelineData.filter(t => t.isRevert).length} reverts, {timelineData.filter(t => t.isCloseEvent).length} closes, {timelineData.filter(t => t.isReopenEvent).length} reopens</span>
      </div>
    </div>
  );
};

export default Lifecycle;